import * as React from 'react';

import LiveComponent from '../components/LiveComponent';
import { Frame } from '../data/Frame';
import Layer from '../data/Layer';
import { getStoreState, storeDispatch } from '../state/Store';
import FrameThumb from './FrameThumb';
import { NewFrameTypeMenu } from './NewFrameTypeMenu';
import { PointData } from '../data/FlyerData';
import { PictureFrame } from 'data/PictureFrame';
import { ChangeLayerFramesRelativeDurationsCommand } from 'state/commands/ChangeLayerFramesRelativeDurationsCommand';
import { SortableElement } from 'react-sortable-hoc';

const SortableFrame = SortableElement(FrameThumb);

const MINIMUM_DRAG_DISTANCE = 10;
const MINIMUM_PORTIION_SIZE = 12;
const THUMB_WIDTH = 60;
interface Props {
  layer: Layer;
  getPlaybackPosition: () => number;
  onSelect?: (frame: Frame) => void;
  onEdit?: () => void;
  canAddFrames: boolean;
  isOver: boolean;
  isSorting: boolean;
}

interface State {
  focusedFrame: Frame | null;
  isResizing: boolean;
}

export class FramesThumbnails extends LiveComponent<Props> {
  state: State = {
    focusedFrame: null,
    isResizing: false
  };

  shouldFocusFrame = false;
  draggedSeparator = 0;
  initialDragPosition: PointData = { x: 0, y: 0 };
  containerEl = React.createRef<HTMLDivElement>();
  minDragX = 0;
  maxDragX = 0;
  editableDuration = 0;

  constructor(props: Props) {
    super(props);

    const { layer } = props;
    this.shouldFocusFrame = layer.frames.length > 1 && !layer.syncPlayback;
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { layer } = this.props;
    this.shouldFocusFrame = layer.frames.length > 1 && !layer.syncPlayback;
  }

  tick() {
    const { frames } = this.props.layer;
    let focusedFrame = null;
    if (this.shouldFocusFrame) {
      const focusedIndex = this.props.layer.visibleFrameIndex;
      focusedFrame = frames[focusedIndex];
    }
    if (focusedFrame !== this.state.focusedFrame) {
      this.setState({ focusedFrame });
    }
  }

  startDraggingSeparator = (index: number, x: number, y: number) => {
    this.draggedSeparator = index;
    const { frames } = this.props.layer;
    this.editableDuration =
      frames[index].relativeDuration + frames[index + 1].relativeDuration;
    this.initialDragPosition = { x, y };
    document.addEventListener('mousemove', this.didMoveMouse);
    document.addEventListener('mouseup', this.didMouseUp);
  };

  getFramesRelativeDurations = (): number[] => {
    const { frames } = this.props.layer;
    return frames.map(f => f.relativeDuration);
  };

  getFramesBoundaries = (): number[] => {
    const result = [];
    const { frames } = this.props.layer;
    let x = 0;
    if (frames.length > 1) {
      const totalFramesWidth = THUMB_WIDTH * frames.length;
      for (let i = 0; i < frames.length; i++) {
        const frameWidth = frames[i].relativeDuration * totalFramesWidth;
        x += frameWidth;
        x += 3;
        result.push(x);
        x += 3;
      }
    } else {
      x += THUMB_WIDTH;
    }
    result.push(x);
    return result;
  };

  didMoveMouse = (e: MouseEvent) => {
    if (!this.state.isResizing) {
      const currentPosition = {
        x: e.clientX,
        y: e.clientY
      };
      if (
        Math.abs(currentPosition.x - this.initialDragPosition.x) >
        MINIMUM_DRAG_DISTANCE
      ) {
        const boundaries = this.getFramesBoundaries();
        this.minDragX =
          this.draggedSeparator === 0
            ? 0
            : boundaries[this.draggedSeparator - 1];
        this.maxDragX = boundaries[this.draggedSeparator + 1];
        this.setState({ isResizing: true });
      }
    }
    if (this.state.isResizing && this.containerEl.current) {
      const containerX = this.containerEl.current.getBoundingClientRect().left;
      let x = e.clientX - containerX;
      x = Math.max(this.minDragX + MINIMUM_PORTIION_SIZE, x);
      x = Math.min(this.maxDragX - MINIMUM_PORTIION_SIZE, x);
      const p = (x - this.minDragX) / (this.maxDragX - this.minDragX);
      const draggedFrameLeftRelativeDuration = p * this.editableDuration;
      const draggedFrameRightRelativeDuration = (1 - p) * this.editableDuration;
      const durations = this.getFramesRelativeDurations();
      durations[this.draggedSeparator] = draggedFrameLeftRelativeDuration;
      durations[this.draggedSeparator + 1] = draggedFrameRightRelativeDuration;
      storeDispatch(
        new ChangeLayerFramesRelativeDurationsCommand(
          this.props.layer.id,
          durations
        )
      );
    }
  };

  didMouseUp = () => {
    document.removeEventListener('mousemove', this.didMoveMouse);
    document.removeEventListener('mouseup', this.didMouseUp);
    this.setState({ isResizing: false });
  };

  renderSeparators = (): JSX.Element => {
    const separators: JSX.Element[] = [];
    const { frames } = this.props.layer;
    const boundaries = this.getFramesBoundaries();
    for (let i = 0; i < frames.length - 1; i++) {
      const x = boundaries[i];
      separators.push(
        <div
          className="separator"
          key={i}
          style={{ transform: `translateX(${Math.round(x)}px)` }}
          onMouseDown={e =>
            this.startDraggingSeparator(i, e.clientX, e.clientY)
          }
        >
          {/* <div className="line" /> */}
          <div className="thumb" />
        </div>
      );
    }
    return <>{separators}</>;
  };

  render() {
    const { selectedFrame, isBeatflyerLite } = getStoreState().app;
    const { frames } = this.props.layer;
    const { canAddFrames, isSorting } = this.props;
    const totalFramesWidth = isBeatflyerLite ? 40 : frames.length * THUMB_WIDTH;
    return (
      <div className="frames-thumbnails-container" ref={this.containerEl}>
        {frames.map((frame, index) => {
          let key = frame.id;
          if (frame instanceof PictureFrame) {
            key = frame.url;
          }
          return (
            <SortableFrame
              data={frame}
              key={key}
              isOver={this.props.isOver}
              onSelect={this.props.onSelect}
              onEdit={this.props.onEdit}
              selected={frame === selectedFrame}
              focused={
                this.shouldFocusFrame ? frame === this.state.focusedFrame : true
              }
              width={totalFramesWidth * frame.relativeDuration}
              index={index}
              disabled={frames.length === 1}
              isResizing={this.state.isResizing}
            />
          );
        })}
        {canAddFrames && <NewFrameTypeMenu layer={this.props.layer} />}
        {this.shouldFocusFrame &&
          this.props.onSelect &&
          (this.props.isOver || this.state.isResizing) &&
          !isSorting &&
          this.renderSeparators()}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/controller/FramesThumbnails.tsx